// --- Utilities defined at top level --- START ---
const STORAGE_KEY = 'bikeSizingData';
const RECOMMENDED_ICON_HTML = `
  <span class="variant-item__recommended-icon" aria-label="Recommended Size" style="display: inline-flex; align-items: center; margin-left: 4px; vertical-align: middle;">
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="20" height="20" rx="10" fill="#248A3D"/>
      <path d="M10.6208 6.271L11.6151 7.26521" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M9.12915 7.7627L10.1234 8.75691" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M7.6377 9.25439L8.63191 10.2486" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M6.146 10.7456L7.14021 11.7398" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4.40576 12.4857L12.3607 4.53076L15.3438 7.51387L7.38887 15.4688L4.40576 12.4857Z" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>
  </span>
`;
const localSelectors = {
  recommendedIcon: '.variant-item__recommended-icon',
  recommendedItemClass: 'variant-buttons__item--recommended',
};

function loadSizingMeasurements() {
  try {
    const rawData = localStorage.getItem(STORAGE_KEY);
    const data = JSON.parse(rawData || '{}');
    // Return the whole object if core measurements exist
    if (data && data.height && data.inseam && data.adjustedHeight) {
      return {
        height: parseFloat(data.height),
        inseam: parseFloat(data.inseam),
        adjustedHeight: parseFloat(data.adjustedHeight),
      };
    } else {
      return null;
    }
  } catch (e) {
    console.error('[ProductInfo SizingUtil] Error loading from local storage:', e);
    return null;
  }
}
// --- Utilities defined at top level --- END ---

// Guard to prevent re-defining the custom element
if (!window.productInfoElementDefined) {
  if (!customElements.get('product-info')) {
    customElements.define(
      'product-info',
      class ProductInfo extends HTMLElement {
        constructor() {
          super();
          // Initialize properties in constructor
          this.abortController = undefined;
          this.onVariantChangeUnsubscriber = undefined;
          this.pendingRequestUrl = null;
          this.preProcessHtmlCallbacks = [];
          this.postProcessHtmlCallbacks = [];
          this.timer = null; // Added for updateMedia interval cleanup
          this.handleRecommendedSizeUpdate = this.selectAndApplyRecommendedVariant.bind(this);
        }

        get variantSelectors() {
          return this.querySelector('variant-selects');
        }

        get productId() {
          return this.getAttribute('data-product-id');
        }

        get sectionId() {
          return this.dataset.originalSection || this.dataset.section;
        }

        get pickupAvailability() {
          // Use single quotes
          return this.querySelector('pickup-availability');
        }

        get quantityInput() {
          return this.querySelector('quantity-input input');
        }

        connectedCallback() {
          this.initializeProductSwapUtility();

          this.onVariantChangeUnsubscriber = window.FoxTheme?.pubsub?.subscribe(
            window.FoxTheme?.pubsub?.PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );

          this.initQuantityHandlers();

          // Defer initial selection slightly to ensure variant DOM is ready
          requestAnimationFrame(() => {
            console.log(
              '[PDP Sizing] Running selectAndApplyRecommendedVariant from connectedCallback via rAF.'
            );
            this.selectAndApplyRecommendedVariant();
          });

          // Add listener for updates from the sizing tool
          window.addEventListener('recommended-size-updated', this.handleRecommendedSizeUpdate);

          // --- Get initial variant state AFTER potential pre-selection (and rAF) --- START ---
          this.currentVariant = this.getSelectedVariant(this);

          if (this.currentVariant) {
            this.updateMedia(this.currentVariant); // Update media for initial variant
            // Note: Icon is already handled by selectAndApplyRecommendedVariant call above
          }

          // Hide SKU row from specifications content if no variant is selected and product has multiple variants
          const urlParams = new URLSearchParams(window.location.search);
          const variantParam = urlParams.get('variant');
          if (
            (!variantParam || variantParam.trim() === '') &&
            this.querySelector('variant-selects')
          ) {
            this.hideSpecificationsSkuRow();
          }
        }

        disconnectedCallback() {
          if (typeof this.onVariantChangeUnsubscriber === 'function') {
            this.onVariantChangeUnsubscriber();
          }
          // Clear any potential interval from updateMedia if element disconnects
          if (this.timer) {
            clearInterval(this.timer);
          }
          // Remove listener for updates from the sizing tool
          window.removeEventListener('recommended-size-updated', this.handleRecommendedSizeUpdate);
        }

        hideSpecificationsSkuRow() {
          const specificationsElem = this.querySelector('[data-product-specifications]');
          if (!specificationsElem) return;

          const specificationsObserver = new MutationObserver((mutationList, observer) => {
            for (const mutation of mutationList) {
              if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                break;
              }
            }

            specificationsElem.querySelectorAll('.easyspecs-spec-row').forEach(row => {
              const label = row.querySelector('.easyspecs-name-cell .easyspec-cell-content');
              if (label?.textContent.trim().toLowerCase() === 'sku') {
                row.style.display = 'none';
              }
            });

            observer.disconnect();
          });

          specificationsObserver.observe(specificationsElem, {
            childList: true,
            subtree: false,
          });
        }

        initializeProductSwapUtility() {
          this.postProcessHtmlCallbacks.push(newNode => {
            window.Shopify?.PaymentButton?.init();
            window.ProductModel?.loadShopifyXR();
          });
        }

        handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
          if (!this.contains(event.target)) return;
          this.resetProductFormState();
          const productUrl =
            target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
          const shouldSwapProduct = this.dataset.url !== productUrl;
          const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
          const viewMode = this.dataset.viewMode || 'main-product';
          const eventSource = event.detail?.source;
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(
              productUrl,
              selectedOptionValues,
              shouldFetchFullPage
            ),
            targetId: eventSource === 'sizing-chart' ? null :target.id,
            callback: shouldSwapProduct
              ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
              : this.handleUpdateProductInfo(productUrl, viewMode, eventSource),
          });
        }

        get productForm() {
          return this.querySelector('form[is="product-form"]');
        }

        resetProductFormState() {
          const productForm = this.productForm;
          productForm?.resetFormState();
        }

        handleSwapProduct(productUrl, updateFullPage, viewMode) {
          return html => {
            const quickView = html.querySelector('#MainProduct-quick-view__content');
            if (quickView && viewMode === 'quick-view') {
              html = quickView.content.cloneNode(true);
            }
            // Use single quotes for outer, double for inner attribute selector
            const selector = updateFullPage ? 'product-info[id^="MainProduct"]' : 'product-info';
            const variant = this.getSelectedVariant(html.querySelector(selector));
            this.updateURL(productUrl, variant?.id);
            if (updateFullPage) {
              document.querySelector('head title').innerHTML =
                html.querySelector('head title').innerHTML;
              // Assume HTMLUpdateUtility is global
              HTMLUpdateUtility?.viewTransition(
                document.querySelector('main'),
                html.querySelector('main'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
              HTMLUpdateUtility?.viewTransition(
                document.getElementById('shopify-section-sticky-atc-bar'),
                html.getElementById('shopify-section-sticky-atc-bar'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            } else {
              HTMLUpdateUtility?.viewTransition(
                this,
                html.querySelector('product-info'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
            this.currentVariant = variant;
          };
        }

        handleUpdateProductInfo(productUrl, viewMode, eventSource) {
          return html => {
            const quickView = html.querySelector('#MainProduct-quick-view__content');
            if (quickView && viewMode === 'quick-view') {
              html = quickView.content.cloneNode(true);
            }
            const variant = this.getSelectedVariant(html);
            this.currentVariant = variant;
            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            this.updateURL(productUrl, variant?.id);
            this.updateShareUrl(variant?.id);
            this.updateVariantInputs(variant?.id);
            if (!variant) {
              this.setUnavailable();
              return;
            }
            this.updateMedia(variant);
            const updateSourceFromDestination = (id, shouldHide = source => false) => {
              const source = html.getElementById(`${id}-${this.sectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            };
            updateSourceFromDestination('price');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Sku-mobile', ({ classList }) =>
              classList.contains('hidden')
            );
            updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('PricePerItem', ({ classList }) =>
              classList.contains('hidden')
            );
            updateSourceFromDestination('Volume');
            this.updateQuantityRules(this.sectionId, this.productId, html);
            this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove(
              'hidden'
            );
            this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');
            // Assume HTMLUpdateUtility is global
            HTMLUpdateUtility?.viewTransition(
              document.querySelector(`#SizeChart-${this.sectionId}`),
              html.querySelector(`#SizeChart-${this.sectionId}`),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
            // Assume FoxTheme is global
            this.toggleAddButton(
              addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
              window.FoxTheme?.variantStrings?.soldOut,
              false,
              addButtonUpdated?.classList.contains('hidden')
            );
            document.dispatchEvent(
              new CustomEvent('variant:changed', { detail: { variant: this.currentVariant, eventSource } })
            );
            // Assume FoxTheme is global
            window.FoxTheme?.pubsub?.publish(
              window.FoxTheme?.pubsub?.PUB_SUB_EVENTS.variantChange,
              {
                data: { sectionId: this.sectionId, html, variant, eventSource },
              }
            );
          };
        }

        buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
          const params = [];
          !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
          if (optionValues.length) {
            params.push(`option_values=${optionValues.join(',')}`);
          }
          return `${url}?${params.join('&')}`;
        }

        getSelectedVariant(productInfoNode) {
          const selectedVariant = productInfoNode.querySelector(
            'variant-selects [data-selected-variant]'
          )?.innerHTML;
          // Fix redundant double negation
          return selectedVariant ? JSON.parse(selectedVariant) : null;
        }

        renderProductInfo({ requestUrl, targetId, callback }) {
          this.abortController?.abort();
          this.abortController = new AbortController();
          fetch(requestUrl, { signal: this.abortController.signal })
            .then(response => response.text())
            .then(responseText => {
              this.pendingRequestUrl = null;
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              callback(html);
            })
            .then(() => {
              document.querySelector(`#${targetId}`)?.focus();
            })
            .catch(error => {
              if (error.name === 'AbortError') {
                console.log('Fetch aborted by user');
              } else {
                console.error(error);
              }
            });
        }

        updateOptionValues(html) {
          const variantSelects = html.querySelector('variant-selects');
          if (variantSelects) {
            // Assume HTMLUpdateUtility is global
            HTMLUpdateUtility?.viewTransition(
              this.variantSelectors,
              variantSelects,
              this.preProcessHtmlCallbacks
            );
            this.addRecommendedVariantIcon(); // Call directly
          }
        }

        updateURL(url, variantId) {
          if (this.dataset.updateUrl === 'false') return;
          window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        }

        updateVariantInputs(variantId) {
          document
            .querySelectorAll(
              `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
            )
            .forEach(productForm => {
              const input = productForm.querySelector('input[name="id"]');
              input.value = variantId ?? '';
              input.dispatchEvent(new Event('change', { bubbles: true }));
            });
        }

        updateMedia(variant) {
          const productMedia = this.querySelector(`[id^='MediaGallery-${this.dataset.section}']`);
          if (!productMedia) return;
          const setActiveMedia = () => {
            if (typeof productMedia.setActiveMedia === 'function') {
              productMedia.init();
              productMedia.setActiveMedia(variant);
              return true;
            }
            return false;
          };
          if (!setActiveMedia()) {
            // Clear previous timer if it exists
            if (this.timer) clearInterval(this.timer);
            this.timer = setInterval(() => {
              if (setActiveMedia()) {
                clearInterval(this.timer);
              }
            }, 100);
          }
        }

        updateShareUrl(variantId) {
          if (!variantId) return;
          const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
          if (!shareButton || !shareButton.updateUrl) return;
          shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
        }

        toggleAddButton(disable = true, text, modifyClass = true, hide = false) {
          const productForm = document.getElementById(`product-form-${this.dataset.section}`);
          if (!productForm) return;
          const addButton = productForm.querySelector('[name="add"]');
          const addButtonText = productForm.querySelector('[name="add"] > span');
          if (!addButton) return;
          if (disable) {
            addButton.setAttribute('disabled', 'disabled');
            if (text) addButtonText.textContent = text;
          } else {
            addButton.removeAttribute('disabled');
            addButtonText.innerHTML = window.FoxTheme?.variantStrings?.addToCart;
          }
          addButton.classList.toggle('hidden', hide);
          if (!modifyClass) return;
        }

        setUnavailable() {
          this.toggleAddButton(true, window.FoxTheme?.variantStrings?.unavailable);
          const price = document.getElementById(`price-${this.dataset.section}`);
          const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
          const sku = document.getElementById(`Sku-${this.dataset.section}`);
          if (price) price.classList.add('hidden');
          if (inventory) inventory.classList.add('hidden');
          if (sku) sku.classList.add('hidden');
        }

        initQuantityHandlers() {
          if (!this.quantityInput) return;
          this.setQuantityBoundries();
          if (!this.hasAttribute('data-original-section')) {
            this.cartUpdateUnsubscriber = window.FoxTheme?.pubsub?.subscribe(
              window.FoxTheme?.pubsub?.PUB_SUB_EVENTS.cartUpdate,
              this.fetchQuantityRules.bind(this)
            );
          }
        }

        setQuantityBoundries() {
          window.FoxTheme?.pubsub?.publish(
            window.FoxTheme?.pubsub?.PUB_SUB_EVENTS.quantityBoundries,
            {
              data: { sectionId: this.sectionId, productId: this.productId },
            }
          );
        }

        fetchQuantityRules() {
          const currentVariantId = this.productForm?.productIdInput?.value;
          if (!currentVariantId) return;
          this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');
          fetch(
            `${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${
              this.sectionId
            }`
          )
            .then(response => response.text())
            .then(responseText => {
              const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
              this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
            })
            .catch(error => {
              console.error(error);
            })
            .finally(() => {
              this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
            });
        }

        updateQuantityRules(sectionId, productId, parsedHTML) {
          if (!this.quantityInput) return;
          window.FoxTheme?.pubsub?.publish(window.FoxTheme?.pubsub?.PUB_SUB_EVENTS.quantityRules, {
            data: { sectionId, productId, parsedHTML },
          });
          this.setQuantityBoundries();
        }

        /**
         * Finds the best matching variant radio input based on adjusted height for PDP context.
         * Assumes data-min-height and data-max-height are accessible from the radio input.
         * @param {number} adjustedHeight - The calculated adjusted height.
         * @returns {HTMLInputElement|null} The best matching variant radio input element or null.
         */
        findBestVariantRadioForPDP(adjustedHeight) {
          const radioInputs = this.querySelectorAll('variant-selects input[type="radio"]');
          if (!radioInputs || radioInputs.length === 0 || isNaN(adjustedHeight)) {
            console.log('[PDP Sizing FindBest]', {
              radioInputsFound: radioInputs?.length,
              adjustedHeight,
            });
            return null;
          }
          console.log(
            `[PDP Sizing FindBest] Checking ${radioInputs.length} radios against height: ${adjustedHeight}`
          );

          let potentialMatches = [];
          let hasInvalidData = false;

          radioInputs.forEach(radioInput => {
            const radioValue = radioInput.value;
            const label = this.querySelector(`label[for="${radioInput.id}"]`);

            // **Important**: Adjust how min/max height are accessed based on PDP HTML structure
            // Example: Accessing directly from radio dataset
            // const minHeightStr = radioInput.dataset.minHeight;
            // const maxHeightStr = radioInput.dataset.maxHeight;
            // Accessing from the associated label's dataset
            const minHeightStr = label?.dataset.minHeight;
            const maxHeightStr = label?.dataset.maxHeight;
            // Example: Accessing from parent item dataset
            // const parentItem = radioInput.closest('.variant-picker__item'); // Example: Adjust selector if needed

            const minHeight = parseFloat(minHeightStr);
            const maxHeight = parseFloat(maxHeightStr);

            if (
              isNaN(minHeight) ||
              isNaN(maxHeight) ||
              minHeightStr === '' ||
              maxHeightStr === ''
            ) {
              // console.warn('Invalid height data for variant radio:', radioInput.value);
              return; // Skip matching this item, flag invalidity
            }

            if (adjustedHeight >= minHeight && adjustedHeight <= maxHeight) {
              const range = maxHeight - minHeight;
              const positionInRange = adjustedHeight - minHeight;
              let percentageInItem = range > 0 ? (positionInRange / range) * 100 : 50;
              percentageInItem = Math.max(0, Math.min(100, percentageInItem));

              potentialMatches.push({
                radioElement: radioInput,
                centrality: Math.abs(percentageInItem - 50),
              });
            }
          });

          if (potentialMatches.length === 0) {
            return null;
          }

          if (potentialMatches.length > 1) {
            potentialMatches.sort((a, b) => a.centrality - b.centrality);
          }

          return potentialMatches[0].radioElement;
        }

        /**
         * Finds the recommended variant based on localStorage, selects the corresponding
         * radio input, dispatches a change event, and ensures the icon is added.
         * This method can be called on initial load or when the recommended size updates.
         */
        selectAndApplyRecommendedVariant() {
          // Check if there's already a variant in the URL - if so, don't pre-select
          const urlParams = new URLSearchParams(window.location.search);
          const hasVariantParam = urlParams.has('variant');

          if (hasVariantParam) {
            // Still add the icon to show which size would be recommended
            this.addRecommendedVariantIcon();
            // Update display text but don't change selection
            const measurements = loadSizingMeasurements();
            const adjustedHeight = measurements?.adjustedHeight;
            let calculationResult = { isFallback: false, variantTitle: null };

            if (typeof adjustedHeight === 'number') {
              const bestRadio = this.findBestVariantRadioForPDP(adjustedHeight);
              if (bestRadio) {
                const label = this.querySelector(`label[for="${bestRadio.id}"]`);
                let title = label?.textContent.trim();
                if (title) {
                  title = title.replace('Variant sold out or unavailable', '').trim();
                }
                title = title || bestRadio.value;
                calculationResult = { isFallback: false, variantTitle: title };
              } else {
                calculationResult = { isFallback: true };
              }
            }

            this.updateSizingToolDisplayText(calculationResult);
            return;
          }

          let variantSelectedThisRun = false;
          const measurements = loadSizingMeasurements();
          const adjustedHeight = measurements?.adjustedHeight;

          let calculationResult = { isFallback: false, variantTitle: null };
          let bestRadio = null;

          if (typeof adjustedHeight === 'number') {
            bestRadio = this.findBestVariantRadioForPDP(adjustedHeight);

            if (bestRadio) {
              const label = this.querySelector(`label[for="${bestRadio.id}"]`);
              let title = label?.textContent.trim();
              if (title) {
                title = title.replace('Variant sold out or unavailable', '').trim();
              }
              title = title || bestRadio.value;
              calculationResult = { isFallback: false, variantTitle: title };

              if (!bestRadio.checked) {
                bestRadio.checked = true;
                variantSelectedThisRun = true;

                const newlySelectedVariant = this.getSelectedVariant(this);
                if (newlySelectedVariant) {
                  this.updateMedia(newlySelectedVariant);
                }

                bestRadio.dispatchEvent(new Event('change', { bubbles: true }));
              } else {
                variantSelectedThisRun = true;
              }
            } else {
              calculationResult = { isFallback: true };
            }
          } else if (measurements) {
            calculationResult = { isFallback: false, variantTitle: null };
          }

          this.addRecommendedVariantIcon();
          this.updateSizingToolDisplayText(calculationResult);
        }

        /**
         * Updates the text content of the initial sizing tool display elements.
         * @param {object} result - Object containing { isFallback: boolean, variantTitle?: string | null }
         */
        updateSizingToolDisplayText(result) {
          const headingEl = document.getElementById('twp-sizing-tool-initial-heading');
          const subtextEl = document.getElementById('twp-sizing-tool-initial-subtext');
          const buttonEl = document.getElementById('initialSizingButton');

          // Ensure elements exist (might not if tool is hidden on some pages)
          if (!headingEl || !subtextEl || !buttonEl) {
            return;
          }

          // Get measurements again for display
          const measurements = loadSizingMeasurements();
          const hasSavedMeasurements = !!measurements;
          const savedHeight = hasSavedMeasurements ? measurements.height.toFixed(1) : null;
          const savedInseam = hasSavedMeasurements ? measurements.inseam.toFixed(1) : null;
          const measurementText =
            savedHeight && savedInseam
              ? `Your measurements: ${savedHeight} cm / ${savedInseam} cm`
              : 'No measurements saved.';

          let headingText = 'Not sure what size is best?';
          let subTextContent =
            "Answer a few short questions and we'll make a recommendation on which size is best for you.";
          let buttonText = 'Find Your Size';

          // Determine text based on the calculated result for *this* product
          if (result.isFallback) {
            headingText = "We can't find a size to suit you";
            subTextContent = measurementText;
            buttonText = 'Change details';
          } else if (result.variantTitle) {
            headingText = `For you we recommend: ${result.variantTitle}`;
            subTextContent = measurementText;
            buttonText = 'Details';
          } else if (hasSavedMeasurements) {
            // Measurements exist, but no specific recommendation for this product (e.g., adjustedHeight missing?)
            headingText = 'Update your recommendation?';
            subTextContent = measurementText;
            buttonText = 'Change details';
          }
          // else: Initial state if no measurements and no result

          headingEl.textContent = headingText;
          subtextEl.textContent = subTextContent;
          buttonEl.textContent = buttonText;
          buttonEl.style.display = ''; // Ensure button is visible (PLP logic is separate)

          // Clean up any potential PLP elements that shouldn't be here
          const slider = document.querySelector('.twp-sizing-tool__container .switch-slider');
          if (slider) slider.remove();
          const changeLink = document.getElementById('changeMeasurementsLink');
          if (changeLink) changeLink.remove(); // Might be inside subtext, setting textContent handles this.
        }

        /**
         * Adds or removes the recommended icon SVG based on the currently stored recommended size.
         * Ensures only the correct variant label has the icon.
         */
        addRecommendedVariantIcon() {
          // Check if utilities are actually loaded
          if (
            typeof loadSizingMeasurements === 'undefined' ||
            typeof RECOMMENDED_ICON_HTML === 'undefined' ||
            typeof localSelectors === 'undefined'
          ) {
            console.log(
              '[PDP Sizing] Icon utilities not found, aborting addRecommendedVariantIcon.'
            );
            return;
          }

          const measurements = loadSizingMeasurements();
          const adjustedHeight = measurements?.adjustedHeight;

          const bestRadioForIcon =
            typeof adjustedHeight === 'number'
              ? this.findBestVariantRadioForPDP(adjustedHeight)
              : null;
          const recommendedRadioValue = bestRadioForIcon?.value;

          const variantLabels = this.querySelectorAll('variant-selects label');
          if (variantLabels.length === 0) {
            return;
          }

          variantLabels.forEach(label => {
            const radioId = label.getAttribute('for');
            if (!radioId) return;

            const radioInput = this.querySelector(`#${radioId}`);
            if (!radioInput || radioInput.type !== 'radio') return;

            const variantTitle = radioInput.dataset.variantTitle || radioInput.value;
            const targetElement = label;
            const iconElement = targetElement.querySelector(localSelectors.recommendedIcon);
            const hasIconClass = targetElement.classList.contains(
              localSelectors.recommendedItemClass
            );

            // Check if this variant is the recommended one
            const isRecommended =
              recommendedRadioValue && radioInput.value === recommendedRadioValue;

            if (isRecommended) {
              // Add icon if it doesn't exist
              if (!iconElement) {
                targetElement.insertAdjacentHTML('beforeend', RECOMMENDED_ICON_HTML);
                targetElement.classList.add(localSelectors.recommendedItemClass);
                targetElement.classList.add('has-recommended-icon'); // Add styling class
              } else {
                // Ensure classes are present if icon already exists (e.g., after DOM update)
                if (!hasIconClass) targetElement.classList.add(localSelectors.recommendedItemClass);
                if (!targetElement.classList.contains('has-recommended-icon'))
                  targetElement.classList.add('has-recommended-icon');
              }
            } else {
              // Remove icon if it exists and this variant is NOT recommended
              if (iconElement) {
                iconElement.remove();
                targetElement.classList.remove(localSelectors.recommendedItemClass);
                targetElement.classList.remove('has-recommended-icon'); // Remove styling class
              } else {
                // Ensure classes are also removed if icon is gone but classes remain
                if (hasIconClass)
                  targetElement.classList.remove(localSelectors.recommendedItemClass);
                if (targetElement.classList.contains('has-recommended-icon'))
                  targetElement.classList.remove('has-recommended-icon');
              }
            }
          });
        }
      } // End class ProductInfo
    );
  } // End if !customElements.get('product-info')

  // Set flag only after successful definition attempt
  window.productInfoElementDefined = true;
}

// Keep the product-promotion-alert definition outside the main guard,
// assuming it might also be loaded multiple times and needs its own guard.
if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();
        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        if (this.buttonEl) {
          // Add null check for safety
          this.buttonEl.addEventListener('click', this.onClick.bind(this), {
            signal: this.abortController.signal,
          });
        }
      }

      onClick(evt) {
        evt.preventDefault();
        this.closest('.product__block')?.classList.add('hidden'); // Add optional chaining
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}

// --- CycleMessages Custom Element Definition START ---
// Check if the element is already defined before defining it
if (!customElements.get('cycle-messages')) {
  class CycleMessages extends HTMLElement {
    constructor() {
      super();

      this.selectors = {
        slide: '[data-cycle-slide]',
        selectedSlide: '.announcement-slide--selected', // Keep existing class for compatibility if needed
        prevButton: '[data-prev-btn]',
        nextButton: '[data-next-btn]',
      };

      this.classes = {
        singleSlide: 'single-slide',
        selectedSlide: 'announcement-slide--selected', // Keep existing class
        slideIn: 'slide-in',
        slideOut: 'slide-out',
      };

      // Initialize properties
      this.intervalId = null;
      this.slides = [];
      this.settings = {};
      this.transitionTiming = 500; // Default

      // Bind visibility change handler
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      // Parse settings from data attribute
      try {
        const settingsData = this.dataset.settings || '{}';
        this.settings = JSON.parse(settingsData);
      } catch (e) {
        console.error('CycleMessages: Error parsing settings data', e, this.dataset.settings);
        this.settings = {}; // Use defaults on error
      }

      // Get settings (now in seconds) and convert to milliseconds
      // Default to 8 seconds autoplay, 0.8 seconds transition if settings are invalid/missing
      this.autoPlaySpeedMs = (Number(this.settings.autoplay_speed) || 8) * 1000;
      this.transitionTimingMs = (Number(this.settings.transition_timing) || 0.8) * 1000;

      // Set CSS variable using milliseconds
      this.style.setProperty('--js-transition-timing', `${this.transitionTimingMs}ms`);

      this.slides = this.querySelectorAll(this.selectors.slide);

      if (this.slides.length <= 1) {
        this.classList.add(this.classes.singleSlide);
        if (this.slides.length === 1) {
          this.slides[0].classList.add(this.classes.selectedSlide);
        }
        return; // No cycling needed for 0 or 1 slide
      }

      // Add visibility change listener to handle tab switching
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.startCycling(); // startCycling will use this.autoPlaySpeedMs
    }

    disconnectedCallback() {
      this.stopCycling();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        // Reset the carousel when tab becomes visible again
        this.resetCarousel();
        this.startCycling();
      } else {
        // Stop cycling when tab is hidden
        this.stopCycling();
      }
    }

    resetCarousel() {
      // Clean up any potential overlap by resetting all slides
      this.slides.forEach(slide => {
        slide.classList.remove(
          this.classes.selectedSlide,
          this.classes.slideIn,
          this.classes.slideOut
        );
      });

      // Make sure only one slide is showing
      if (this.slides.length > 0) {
        // Find current selected slide or default to first
        const currentSelected = this.querySelector(this.selectors.selectedSlide) || this.slides[0];
        currentSelected.classList.add(this.classes.selectedSlide);
      }
    }

    startCycling() {
      this.stopCycling(); // Clear any existing interval

      // Ensure first slide is visible initially without transition
      if (this.slides.length > 0 && !this.querySelector(this.selectors.selectedSlide)) {
        this.slides[0].classList.add(this.classes.selectedSlide);
      }

      // Use milliseconds for setInterval
      if (this.autoPlaySpeedMs > 0) {
        this.intervalId = setInterval(() => {
          this.cycleSlides();
        }, this.autoPlaySpeedMs); // Use the Ms value
      }
    }

    stopCycling() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    cycleSlides() {
      if (this.slides.length <= 1) return;

      const selectedSlide = this.querySelector(this.selectors.selectedSlide);
      let nextSlide;

      if (selectedSlide) {
        const currentIndex = Array.from(this.slides).indexOf(selectedSlide);
        const nextIndex = (currentIndex + 1) % this.slides.length;
        nextSlide = this.slides[nextIndex];

        // Immediately remove selected class and add slide-out class
        selectedSlide.classList.remove(this.classes.selectedSlide);
        selectedSlide.classList.add(this.classes.slideOut);

        // Listener now only needs to remove the slide-out class
        selectedSlide.addEventListener(
          'transitionend',
          () => {
            selectedSlide.classList.remove(this.classes.slideOut); // Only remove slide-out
          },
          { once: true }
        );
      } else {
        nextSlide = this.slides[0];
      }

      // Add slide-in class to the NEXT slide (briefly)
      nextSlide.classList.add(this.classes.slideIn);

      // Use requestAnimationFrame for smooth transition
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          nextSlide.classList.remove(this.classes.slideIn);
          nextSlide.classList.add(this.classes.selectedSlide);
        });
      });
    }
  }

  customElements.define('cycle-messages', CycleMessages);
}
// --- CycleMessages Custom Element Definition END ---

// TWP addition: description toggle
function initializeDescriptionToggle(container) {
  container.querySelectorAll('.product__description--collapsible').forEach(wrapper => {
    const contentWrapper = wrapper.querySelector('[data-description-content]');
    const contentElement = contentWrapper ? contentWrapper.querySelector('p') : null;
    const button = wrapper.querySelector('[data-read-more-button]');

    if (!contentWrapper || !contentElement || !button) {
      if (button) button.remove();
      return;
    }

    const readMoreText = button.dataset.readMoreText;
    const readLessText = button.dataset.readLessText;

    requestAnimationFrame(() => {
      const clampedPHeight = contentElement.clientHeight;

      const originalWrapperClampStyle = contentWrapper.style.webkitLineClamp;
      contentWrapper.style.webkitLineClamp = 'unset';
      const originalWrapperOverflowStyle = contentWrapper.style.overflow;
      contentWrapper.style.overflow = 'visible';

      requestAnimationFrame(() => {
        const fullPHeight = contentElement.scrollHeight;

        contentWrapper.style.webkitLineClamp = originalWrapperClampStyle || '';
        contentWrapper.style.overflow = originalWrapperOverflowStyle || '';

        const isOverflowing = fullPHeight > clampedPHeight + 1;

        if (isOverflowing) {
          button.classList.add('is-visible');
        } else {
          button.remove();
          return;
        }

        if (!button.dataset.listenerAttached) {
          button.addEventListener('click', () => {
            const isExpanded = wrapper.classList.toggle('is-expanded');
            button.setAttribute('aria-expanded', isExpanded);
            button.textContent = isExpanded ? readLessText : readMoreText;
          });
          button.dataset.listenerAttached = 'true';
        }
      });
    });
  });
}

// --- Event Listeners (DOMContentLoaded, shopify:section:load) remain the same ---
document.addEventListener('DOMContentLoaded', () => {
  const productInfoContainer = document.querySelector('.product__info-container');
  if (productInfoContainer) {
    initializeDescriptionToggle(productInfoContainer);
  }
});

document.addEventListener('shopify:section:load', event => {
  // Check if the specific element exists within the loaded section
  if (
    event.target.querySelector &&
    event.target.querySelector('.product__description--collapsible')
  ) {
    initializeDescriptionToggle(event.target);
  }
});
